import React, { useState, useEffect, useRef } from "react";
import "assets/css/reorder.css";
import ArrowIcon1 from "assets/images/images_new/arrow-icon.svg";
import TimeUp from "components/PracticeMaterial/PopUps/TimeUp";
// dnd-kit imports
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  rectIntersection
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove as dkArrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const IDEAL_TIME = 120; // SECONDS.

const EMPTY_BOARD = {
  columns: [
    { id: 1, title: 'Source', cards: [] },
    { id: 2, title: 'Target', cards: [] }
  ]
};

const Reorder = (props) => {
  const [questionId, setQuestionId] = useState("");
  const [reset, setReset] = useState(false);
  const [is_answer, setIs_answer] = useState(false);
  const [items, setItems] = useState([]);
  const [sortedItems, setSortedItems] = useState([]);

  const [board, setBoard] = useState({
    columns: [
      { id: 1, title: 'Source', cards: [] },
      { id: 2, title: 'Target', cards: [] }
    ]
  });

  const [resultObj, setResultObj] = useState({});
  const [correctFlag, setCorrectFlag] = useState(true);
  const [idealTime, setIdealTime] = useState(IDEAL_TIME);
  const [idealTimeInterval, setIdealTimeInterval] = useState(null);
  const [idealTimeExceed, setIdealTimeExceed] = useState(false);

  const idealTimeIntervalRef = useRef(null);
  const [activeId, setActiveId] = useState(null);

  const { data, resultMode, answer, showResponse } = props;

  const emptyBoard = () => {
    return EMPTY_BOARD;
  }

  // Initialize board with data
  const initializeBoard = () => {
    if (!props.data || !props.data.option || !Array.isArray(props.data.option)) {
      return emptyBoard();
    }

    const { option } = props.data;

    if (option && option.length > 0) {
      return {
        columns: [
          {
            id: 1,
            title: 'Source',
            cards: option.map((item, index) => {
              return {
                id: item.id ?? index,
                title: item.options,
                description: item.options,
                content: item.options
              }
            }),
          },
          {
            id: 2,
            title: 'Target',
            cards: []
          }
        ]
      };
    }

    return emptyBoard();
  }

  // Initialize items
  const initializeItems = () => {
    if (!props.data || !props.data.option || !Array.isArray(props.data.option)) {
      return {
        items: [],
        sortedItems: []
      };
    }

    const { option } = props.data;

    let data = {
      items: [],
      sortedItems: []
    }

    if (items.length === 0) {
      data.items = option;
      data.sortedItems = option.slice().sort(function (a, b) {
        return a.index - b.index
      });
    }

    return data;
  }

  useEffect(() => {
    if (answer && idealTimeIntervalRef.current) {
      clearInterval(idealTimeIntervalRef.current);
      idealTimeIntervalRef.current = null;
    }
  }, [answer]);

  // Start idealtime countdown
  const countDownToIdealTime = () => {
    const { answer } = props;

    if (answer) {
      clearInterval(idealTimeIntervalRef.current); // stop timer when submitted.
    } else {
      setIdealTime(prevIdealTime => {
        if (prevIdealTime > 0) {
          return prevIdealTime - 1;
        } else {
          clearInterval(idealTimeIntervalRef.current);
          setIdealTimeExceed(true);
          return 0;
        }
      });
    }
  }

  const idealTimeExceedToggle = () => {
    setIdealTimeExceed(prevState => !prevState);
  }

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const customCollisionDetection = (args) => {
    const { active, droppableContainers, ...rest } = args;
    // Exclude the droppable that belongs to the active draggable itself
    const filteredContainers = droppableContainers.filter(
      (container) => container.id !== active.id,
    );
    // Use the standard closestCenter algorithm on the filtered list
    return closestCenter({
      ...rest,
      active,
      droppableContainers: filteredContainers,
    });
  };

  // const magicFunction = (arr, old_index, new_index) => {
  //   if (new_index >= arr.length) {
  //     var k = new_index - arr.length + 1;
  //     while (k--) {
  //       arr.push(undefined);
  //     }
  //   }
  //   arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  //   return arr;
  // }

  // Keep same cardDragEnd signature as before. This function will be called by dnd logic below.
  const cardDragEnd = (boardOrCard, card, source, destination) => {
    if (is_answer) return;

    let updatedBoard;

    // Check if first parameter is the updated board (compatibility with old API)
    if (boardOrCard && boardOrCard.columns) {
      updatedBoard = boardOrCard;
    } else {
      // First parameter is the card, need to manually update board
      // card: { id, fromColumnId, fromPosition }
      // source: { toColumnId, toPosition }

      updatedBoard = JSON.parse(JSON.stringify(board)); // Deep clone

      const sourceCol = updatedBoard.columns.find(col => col.id === card.fromColumnId);
      const targetCol = updatedBoard.columns.find(col => col.id === source.toColumnId);

      if (!sourceCol || !targetCol) {
        console.error('Column not found');
        return;
      }

      // Remove card from source
      const [movedCard] = sourceCol.cards.splice(card.fromPosition, 1);

      // Add to target at requested position
      // If toPosition is -1 or undefined, append
      const insertIndex = (typeof source.toPosition === "number" && source.toPosition >= 0) ? source.toPosition : targetCol.cards.length;
      targetCol.cards.splice(insertIndex, 0, movedCard);
    }

    setBoard(updatedBoard);
    props.showResult?.();

    const targetColumn = updatedBoard.columns.find(col => col.id === 2);
    const answerIds = targetColumn?.cards?.map(c => c.id) || [];
    const correctIds = sortedItems.map(c => c.id);

    const dataToSubmit = { answer: answerIds, correct: correctIds };
    props.submit?.(dataToSubmit, true);
    setResultObj(dataToSubmit);
  };

  const updateCorrect = () => {
    if (Array.isArray(sortedItems) && sortedItems.length !== 0) {
      props.submit({ 'answer': [], 'correct': sortedItems.sort(function (a, b) { return a.index - b.index }).map((val) => { return val.id; }) });
      setCorrectFlag(false);
      setResultObj({ 'answer': [], 'correct': sortedItems.sort(function (a, b) { return a.index - b.index }).map((val) => { return val.id; }) });
    }
  }

  // const stopTest = () => {
  //   props.nextQuestion(resultObj);
  // }

  useEffect(() => {
    const initialBoard = initializeBoard();
    const initialItems = initializeItems();

    const interval = setInterval(countDownToIdealTime, 1000);
    idealTimeIntervalRef.current = interval;
    setIdealTimeInterval(interval);
    setBoard(initialBoard);
    setItems(initialItems.items);
    setSortedItems(initialItems.sortedItems);

    return () => {
      clearInterval(idealTimeIntervalRef.current);
    };
  }, [props.data]);
  useEffect(() => {
    if (props.answer) {
      setIs_answer(true);
    }
  }, [props.answer]);

  // getDerivedStateFromProps equivalent - handle props changes
  useEffect(() => {
    let temp = [];
    if (props?.data && props?.data?.option && Array.isArray(props?.data?.option)) {
      if ((questionId && questionId !== props?.data?.id) || props?.restart) {
        const { option } = props?.data;
        temp = props?.data?.option?.slice();
        let newBoard = {
          columns: [
            {
              id: 1,
              title: 'Source',
              cards: option?.map((item) => {
                return {
                  id: item?.id,
                  title: item?.options,
                  description: item?.options,
                  content: item?.options
                }
              }),
            },
            {
              id: 2,
              title: 'Target',
              cards: []
            }
          ]
        };

        // cleanup old DOM text if any (kept from your original)
        if (document.querySelectorAll('div[data-rbd-draggable-id="lane-draggable-1"] > div > div:first-child').length > 0) {
          document.querySelector('div[data-rbd-draggable-id="lane-draggable-1"] > div > div:first-child').innerHTML = '<span>Source</span>';
        }

        setCorrectFlag(true);
        setQuestionId(props?.data?.id);
        setReset(true);
        setItems(props?.data?.option);
        setBoard(newBoard);
        setSortedItems(temp.sort(function (a, b) {
          return a.index - b.index
        }));
      } else {
        if (!questionId) {
          setCorrectFlag(true);
          setQuestionId(props?.data?.id);
        }
      }
    }
  }, [props.data, props.restart, questionId]);

  // Handle answer state
  useEffect(() => {
    if (props.answer && sortedItems.length > 0) {
      const newBoard = {
        columns: [
          {
            id: 1,
            title: 'Correct Order',
            cards: sortedItems.map(item => ({
              id: item.id,
              title: item.options,
              description: item.options,
              content: item.options
            }))
          },
          {
            ...board.columns[1],
            title: 'Your Response'
          }
        ]
      };

      setBoard(newBoard);

      // Wait for DOM to render and then apply classes to target card elements (kept from original)
      setTimeout(() => {
        const targetColumnCards = document.querySelectorAll(
          'div[data-rbd-draggable-id="lane-draggable-2"] .source-target-list'
        );

        const targetAnswer = resultObj.answer || [];
        const correctAnswer = resultObj.correct || [];
        
        // Clear previous classes and add cross by default
        targetColumnCards.forEach(card => {
          card.classList.remove('correct', 'wrong', 'not-selected');
          card.classList.add('cross');
        });

        // Apply pair-based logic from working code
        for (let i = 0; i < targetAnswer.length - 1; i++) {
          for (let j = 0; j < correctAnswer.length - 1; j++) {
            if (correctAnswer.length > i + 1) {
              const targetPair = targetAnswer[i] + '-' + targetAnswer[i + 1];
              const correctPair = correctAnswer[j] + '-' + correctAnswer[j + 1];
              if (targetPair === correctPair) {
                targetColumnCards[i]?.classList.remove('cross');
              }
            }
          }
        }

      }, 10000);
    }
  }, [props.answer, sortedItems, resultObj]);

  useEffect(() => {
    if (props.restart) {
      props.restartQue();
    }

    if (props.data) {
      if (correctFlag) {
        updateCorrect();
      }
    }

    if (reset) {
      setReset(false);
      setIs_answer(false);
    }
  },[props]);

  const getCardStatusByPair = (cardId, index) => {
    const targetAnswer = resultObj.answer || [];
    const correctAnswer = resultObj.correct || [];
        
    // Not selected but exists in correct sequence → blue
    if (!targetAnswer.includes(cardId) && correctAnswer.includes(cardId)) {
      return "not-selected";
    }

    // Find pairs in targetAnswer
    for (let i = 0; i < targetAnswer.length - 1; i++) {
      const pair = [targetAnswer[i], targetAnswer[i + 1]];
      
      // Check if current card is part of this pair
      if (pair.includes(cardId)) {
        const correctPairIndex = correctAnswer.indexOf(pair[0]);
        if (
          correctPairIndex !== -1 &&
          correctAnswer[correctPairIndex] === pair[0] &&
          correctAnswer[correctPairIndex + 1] === pair[1]
        ) {
          return "correct"; // green
        } else {
          return `wrong ${is_answer ? 'cross':''}`; // red
        }
      }
    }

    // For last card (if single at end)
    if (targetAnswer[targetAnswer.length - 1] === cardId) {
      return correctAnswer[correctAnswer.length - 1] === cardId ? "correct" : `wrong ${is_answer ? 'cross':''}`;
    }

    return "";
  };

  /* ---------------------
     dnd-kit board rendering
     Option A: Source -> Target only (Source static; Target sortable)
     ---------------------- */

  const getCardResultStatusByPair = (cardId, index) => {
    const correctSequence = resultObj.correct || [];
    const targetSequence = resultObj.answer || [];
    
    // Start with cross (wrong) by default
    let isCorrect = false;
    
    // Check if this card is part of a correct pair
    for (let i = 0; i < targetSequence.length - 1; i++) {
      for (let j = 0; j < correctSequence.length - 1; j++) {
        if (correctSequence.length > i + 1) {
          const targetPair = targetSequence[i] + '-' + targetSequence[i + 1];
          const correctPair = correctSequence[j] + '-' + correctSequence[j + 1];
          if (targetPair === correctPair && i === index) {
            isCorrect = true;
            break;
          }
        }
      }
      if (isCorrect) break;
    }
    
    return isCorrect ? "" : "cross";
  };

  const sensors = useSensors(
    useSensor(PointerSensor,{
    activationConstraint: {
      distance: 3,
    },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // helper: card id string conventions used by dnd-kit in this component
  const cardId = (id) => `card-${id}`;
  const parseCardId = (str) => {
    if (!str) return null;
    if (typeof str === "string" && str.startsWith("card-")) {
      return str.slice(5);
    }
    return null;
  };

  // Droppable column wrapper (for column-level drops)
  function ColumnDrop({ col, children }) {
    const { isOver, setNodeRef } = useDroppable({ id: `col-${col.id}` });
    return (
      <div ref={setNodeRef} className={`column-wrapper d-flex flex-column`} >
          <h5>{col.title}</h5>
          {children}
      </div>
    );
  }

  // Source card (draggable but not sortable)
  function SourceCard({ card, disabled }) {
    const idStr = cardId(card.id);
    const { attributes, listeners, setNodeRef, transform, isDragging, transition } = useDraggable({
      id: idStr,
      data: { fromColumnId: 1, fromPosition: board.columns[0].cards.findIndex(c => `${c.id}` === `${card.id}`) },
      disabled
    });

    // FIXED SIZE DURING DRAG
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0 : 1,
      pointerEvents: isDragging ? 'none' : 'auto',
      cursor: disabled ? "default" : "grab",
      width: '100%',  // set your card width
      minHeight: '60px', // set min height if needed
      // maxHeight: '130px',
      // minWidth: '200px',
      boxSizing: 'border-box'
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <div className={`source-target-list d-flex justify-content-between ${props.answer ? "correct" : ""}`}>
          <p>{card.content || card.description || card.title || 'No content'}</p>
          {!answer && <img src={ArrowIcon1} alt="LA" />}
        </div>
      </div>
    );
  }

  // Target card (sortable)
  function TargetCard({ card, disabled }) {
    const idStr = cardId(card.id);
    const { attributes, listeners, setNodeRef, transform, isDragging, transition } = useSortable({
      id: idStr,
      data: { fromColumnId: 1, fromPosition: board.columns[0].cards.findIndex(c => `${c.id}` === `${card.id}`) },
      disabled
    });

    // FIXED SIZE DURING DRAG
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.85 : 1,
      pointerEvents: isDragging ? 'none' : 'auto',
      cursor: disabled ? "default" : "grab",
      width: '100%',  // set your card width
      minHeight: '60px', // set min height if needed
      // maxHeight: '130px',
      // minWidth: '450px',
      boxSizing: 'border-box'
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <div className={`source-target-list d-flex justify-content-between ${props.answer ? getCardStatusByPair(card.id) : ""}`}>
          <p>{card.content || card.description || card.title || 'No content'}</p>
          {!answer && <img src={ArrowIcon1} alt="LA" />}
        </div>
      </div>
    );
  }

  // onDragEnd logic: move item from Source -> Target or reorder inside Target, or move back to Source
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active) {
      setActiveId(null);
      return;
    }

    // active.id is like "card-<id>"
    const draggedCardId = parseCardId(active.id);
    if (!draggedCardId) return;

    // Find where the dragged card currently resides
    let fromColumnId = null;
    let fromPosition = -1;
    board.columns.forEach((col) => {
      const idx = col.cards.findIndex(c => `${c.id}` === `${draggedCardId}`);
      if (idx > -1) {
        fromColumnId = col.id;
        fromPosition = idx;
      }
    });

    // If no over target (dropped outside), do nothing
    if (!over || !over.id) return;

    // If dropped over a column wrapper e.g., "col-2"
    if (typeof over.id === "string" && over.id.startsWith("col-")) {
      const toColId = Number(over.id.split("-")[1]);

      // if dropped into same column and source == target and it's Source column, nothing to do
      if (fromColumnId === toColId && toColId === 1) return;

      // default to append
      const toPosition = (board.columns.find(c => c.id === toColId)?.cards?.length) ?? 0;
      // console.log("To Position : ",toPosition," fromColumnId ",fromColumnId," toColId ",toColId);
      
      // call your cardDragEnd API: (boardOrCard, card, source, destination)
      cardDragEnd(null, { id: draggedCardId, fromColumnId, fromPosition }, { toColumnId: toColId, toPosition });
      return;
    }

    // If dropped over another card (e.g., "card-123")
    // const overCardId = parseCardId(over.id);
    // if (!overCardId) return;

    // // Determine destination column & index where 'overCardId' is
    // let toColumnId = null;
    // let toPosition = -1;
    // // board.columns.forEach((col) => {
    // //   const idx = col.cards.findIndex(c => `${c.id}` === `${overCardId}`);
    // //   if (idx > -1) {
    // //     toColumnId = col.id;
    // //     toPosition = idx;
    // //   }
    // // });
    // const isBelowOverItem = active.rect.current.translated && active.rect.current.translated.top > over.rect.top + over.rect.height / 2;
    // console.log("isBelowOverItem->",isBelowOverItem);
    
    //   board.columns.forEach((col) => {
    //   const idx = col.cards.findIndex(c => `${c.id}` === `${overCardId}`);
    //   if (idx > -1) {
    //     toColumnId = col.id;

    //     const isLastCard = idx === col.cards.length - 1;
    //     // toPosition = isLastCard ? col.cards.length : idx;
    //     let insertIndex = isLastCard ? col.cards.length : idx;

    //     // 🔥 IMPORTANT FIX
    //     if (fromColumnId === toColumnId && fromPosition < insertIndex) {
    //       insertIndex -= 1;
    //     }

    //     toPosition = insertIndex;
    //   }
    // });

    // if (toColumnId === null) return;

    // // If dragged within same column and it's target column -> reorder
    // if (fromColumnId === toColumnId && toColumnId === 2) {
    //   // reorder inside target
    //   const updatedBoard = JSON.parse(JSON.stringify(board));
    //   const col = updatedBoard.columns.find(c => c.id === 2);
    //   const fromIdx = fromPosition;
    //   const toIdx = toPosition;

    //   if (fromIdx === -1 || toIdx === -1) return;

    //   col.cards = dkArrayMove(col.cards, fromIdx, toIdx);
    //   setBoard(updatedBoard);

    //   // call onCardDragEnd similarly to previous behavior
    //   // send card id and positions
    //   cardDragEnd(null, { id: draggedCardId, fromColumnId, fromPosition }, { toColumnId, toPosition });
    //   return;
    // }
    
    // // If moving from Source -> Target (or Target -> Source) and dropped over a card in other column
    // // call cardDragEnd accordingly
    // if (fromColumnId !== toColumnId) {
    //   // toPosition +=1;
    //   // console.log("fromColumnId ",fromColumnId ,"fromPosition", fromPosition, toColumnId, "<- toColumnId, toPosition->" ,toPosition );
    //   cardDragEnd(null, { id: draggedCardId, fromColumnId, fromPosition }, { toColumnId, toPosition });
    //   return;
    // }

    const overCardId = parseCardId(over.id);
    if (!overCardId) return;

    let toColumnId = null;
    let targetIndex = -1;

    board.columns.forEach((col) => {
      const idx = col.cards.findIndex((c) => `${c.id}` === `${overCardId}`);
      if (idx > -1) {
        toColumnId = col.id;
        targetIndex = idx;
      }
    });

    if (toColumnId === null) return;

    // 🔥 Detect before / after
    const isBelowOverItem =
      active.rect.current.translated &&
      active.rect.current.translated.top > over.rect.top + over.rect.height * 0.1;

    let insertIndex = targetIndex + (isBelowOverItem ? 1 : 0);

    // 🔥 Fix same-column downward drag
    if (fromColumnId === toColumnId && fromPosition < insertIndex) {
      insertIndex -= 1;
    }
    // --- NEW: handle reorder inside Target column with dkArrayMove ---
    if (fromColumnId === toColumnId && toColumnId === 2) {
      // Reorder within the target column
      const updatedBoard = JSON.parse(JSON.stringify(board));
      const targetCol = updatedBoard.columns.find(c => c.id === 2);
      targetCol.cards = dkArrayMove(targetCol.cards, fromPosition, insertIndex);

      setBoard(updatedBoard);

      // Submit updated answer
      const answerIds = targetCol.cards.map(c => c.id);
      const dataToSubmit = { answer: answerIds, correct: sortedItems.map(c => c.id) };
      props.submit?.(dataToSubmit, true);
      setResultObj(dataToSubmit);
      props.showResult?.();

      setActiveId(null);
      return; // exit early
    }

    cardDragEnd(
      null,
      { id: draggedCardId, fromColumnId, fromPosition },
      { toColumnId, toPosition: insertIndex }
    );
    // Otherwise nothing
    setActiveId(null);
  };

  // onDragEnd logic: move item from Source -> Target or reorder inside Target, or move back to Source
  // style={{ minHeight: "400px", background: "#ffffff" }}
  return (
    <div className="reorder-board-new" >
      {data && !resultMode && (
        <>
          <div className="visibility-visible mx-0 w-100">
            {board &&
            Array.isArray(board.columns) &&
            board.columns.length > 0 ? (
              <>
                <DndContext
                  sensors={sensors}
                  collisionDetection={rectIntersection}
                  onDragStart={handleDragStart}
                  onDragCancel={handleDragCancel}
                  onDragEnd={handleDragEnd}
                  modifiers={[restrictToWindowEdges]}
                >
                  <div className="d-flex reorder-flex-container flex-lg-row w-100">
                    <ColumnDrop col={board.columns[0]} className="column-drop">
                      <div className="source-list">
                        {board.columns[0].cards.map((card) => (
                          <SourceCard
                            key={card.id}
                            card={card}
                            disabled={!!answer || is_answer}
                          />
                        ))}
                      </div>
                    </ColumnDrop>

                    <ColumnDrop col={board.columns[1]} className="column-drop">
                      {is_answer ? (
                        <ul className="connectList">
                          {board.columns && board.columns[1].cards.map((v, k) => {
                            return <li key={k} style={{color:'#59595c'}} className={`list-group-item1 source-target-list ${getCardResultStatusByPair(v.id,k)}`}>{v.description}</li>
                          })}
                        </ul>
                      ) : (
                        <SortableContext
                          items={board.columns[1].cards.map((c) => cardId(c.id))}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="target-list">
                            {board.columns[1].cards.map((card) => (
                              <TargetCard
                                key={card.id}
                                card={card}
                                disabled={!!answer || is_answer}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      )}
                      {/* <SortableContext
                        items={board.columns[1].cards.map((c) => cardId(c.id))}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="target-list">
                          {board.columns[1].cards.map((card) => (
                            <TargetCard
                              key={card.id}
                              card={card}
                              disabled={!!answer || is_answer}
                            />
                          ))}
                        </div>
                      </SortableContext> */}
                    </ColumnDrop>
                  </div>
                  {/* ADD THIS DRAGOVERLAY COMPONENT */}
                  <DragOverlay>
                    {activeId ? (
                      <div
                        style={{
                          width: '100%', // Fixed width during drag
                          minHeight: "60px",
                          maxHeight: "130px",
                          opacity: 0.9,
                          cursor: "grabbing",
                          minWidth: '450px',
                        }}
                      >
                        <div className="source-target-list">
                          <p>
                            {(() => {
                              const cardIdVal = parseCardId(activeId);
                              let foundCard = null;
                              board.columns.forEach((col) => {
                                const c = col.cards.find(
                                  (card) => `${card.id}` === `${cardIdVal}`
                                );
                                if (c) foundCard = c;
                              });
                              return (
                                foundCard?.content ||
                                foundCard?.description ||
                                foundCard?.title ||
                                "Dragging..."
                              );
                            })()}
                          </p>
                          <img src={ArrowIcon1} alt="LA" />
                        </div>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
              </>
            ) : (
              <p>Loading board...</p>
            )}
          </div>


          <TimeUp
            isOpen={idealTimeExceed}
            toggle={idealTimeExceedToggle}
            title="Time Alert"
            message="You have spent 2 minutes on [Reorder Paragraph], which is the recommended time. Click Submit Now to submit your response, or click on Cross to ignore."
            buttonLabel="Submit Now"
            showResponse={showResponse}
            submitDisable={props.submitDisable}
          />
        </>
      )}

      
      {/* <div >
        {is_answer && !props.resultMode && <h3>Result: </h3>}
        {is_answer &&
          <div className="col-lg-6">
            <ul className="connectList">
              {board.columns && board.columns[1].cards.map((v, k) => {
                return <li key={k} className="list-group-item1">{v.description}</li>
              })}
            </ul>
          </div>
        }
      </div> */}
    </div>
  );
}

export default Reorder;
